/**
 * Custom hook for managing bookings in a hotel management system.
 * This hook provides various functions for handling check-in, check-out, room blocking, and other booking-related operations.
 *
 * @returns {IUseBookings} An object containing various functions and state for managing bookings.
 */
import { useDeclareNewDayContext } from '@modules/front-office/components/declare-new-day/fo-dnd-context';
import { GridRowSelectionModel } from '@mui/x-data-grid';
import { SHA512 } from 'crypto-js';
import { useState } from 'react';
import { usePostCASTransactionMutation } from '../api/cas/cas.api.rtk';
import {
  BookingsParamsType,
  useCancelBookingMutation,
  useCheckInBookingMutation,
  useDeleteBookingSharerMutation,
  usePostBookingConfirmStatusMutation,
  usePostBookingSharerMutation,
  usePostCheckOutMutation,
  usePostRoomBlockingMutation,
  usePostUnblockRoomBlockingMutation,
  usePutRoomBlockingMutation,
  usePutUndoCheckInMutation,
  usePutUndoCheckOutMutation,
} from '../api/front-office/front-office.api.rtk';
import { CASPostTransaction } from '../interfaces/cas.interface';
import { CancelReservationReason } from '../modules/front-office/components/reservation/fo-reservation-cancel-reason-modal';
import {
  IFOBookingCheckInPayload,
  FOCreateBlockingsPayloadInterface,
  FOCreateBookingSharerPayloadInterface,
  FODeleteBookingSharerPayloadInterface,
} from '../modules/front-office/interface/fo-api-interface';
import { CheckOutTransactionBody } from '../modules/front-office/interface/room-posting-interface';
import { useAppSelector } from '../store';
import { isValidUUID } from '../utils/format';
import useAppContext from './useAppContext';
import useDisclosure, { UseDisclosureInterface } from './useDisclosure';
import useToast from './useToast';
import { useCurrentModule } from './useCurrentModule';

export interface IUseBookings {
  handleCheckOut: (
    skipValidationDate?: boolean,
    skipValidationTraces?: boolean,
    sendFolioToEmail?: boolean,
    skipPostSI?: boolean,
  ) => Promise<boolean | undefined>;
  handlePendingCheckout: (
    skipValidationDate?: boolean,
    skipValidationTraces?: boolean,
  ) => Promise<boolean | undefined>;
  setSelectedTransactionId: (id: string) => void;
  balanceMessage: string;
  tracesMessage: string;
  tracesDialogDisclosure: UseDisclosureInterface;
  scheduleDisclosure: UseDisclosureInterface;
  printFolioDialogDisclosure: UseDisclosureInterface;
  balanceDialogDisclosure: UseDisclosureInterface;
  isLoadingCheckOut: boolean;
  handleCheckIn: (
    roomNumber?: string,
    checkinDate?: string,
    checkoutDate?: string,
    obj?: { refId: string },
  ) => Promise<boolean>;
  handleUndoCheckOut: (
    bookingRefId: string,
    bookingStatus: BookingsParamsType,
  ) => Promise<string>;
  handleUndoCheckIn: (
    bookingRefId: string,
    roomStatus: string,
  ) => Promise<string>;
  handleMultiCheckIn: (
    bookings: string[] | GridRowSelectionModel,
  ) => Promise<void>;
  handleUnblockingRoom: (param: string) => Promise<boolean>;
  handleCreateRoomBlocking: (
    param: FOCreateBlockingsPayloadInterface['properties'][0]['property']['blockings'],
  ) => Promise<boolean>;
  handleUpdateRoomBlocking: (data: {
    id: string;
    payload: FOCreateBlockingsPayloadInterface['properties'][0]['property']['blockings'];
  }) => Promise<boolean>;
  handleSaveBookingSharer: (data: {
    id: string;
    payload: FOCreateBookingSharerPayloadInterface['properties'][0]['property']['sharers'];
  }) => Promise<boolean>;
  handleDeleteBookingSharer: (data: {
    id: string;
    payload: FODeleteBookingSharerPayloadInterface['properties'][0]['property']['sharers'];
  }) => Promise<boolean>;
  handleCancelBookings: (
    data: CancelReservationReason,
    referenceId: string,
  ) => Promise<boolean>;
  handleConfirmStatusBooking: (
    referenceId: string,
    status: boolean,
  ) => Promise<boolean>;
  printSIDialogDisclosure: UseDisclosureInterface;
  siNo: string;
  printVSIDialogDisclosure: UseDisclosureInterface;
  vsiNo: string;
  messageDialogDisclosure: UseDisclosureInterface;
  messageDialogTitle: string;
  messageDialogDesc: string;
  messageDialogStatus: string;
}

/**
 * Custom hook for managing bookings.
 *
 * @returns {IUseBookings} An object containing various functions and state for managing bookings.
 */
const useBookings = (props?: {
  refreshList: () => Promise<void>;
}): IUseBookings => {
  const {
    openSecCodeDialog,
    startLoading,
    stopLoading,
    genpar: { getGenparValue },
  } = useAppContext();

  const withLockset = getGenparValue('with_lockset') === 'true';

  const { toast } = useToast();
  const [postCheckOut, { isLoading: isLoadingCOut }] =
    usePostCheckOutMutation();
  const [checkIn] = useCheckInBookingMutation();
  const [putUndoCheckOut] = usePutUndoCheckOutMutation();
  const [putUndoCheckIn] = usePutUndoCheckInMutation();
  const [unblockRoom] = usePostUnblockRoomBlockingMutation();
  const [createBlocking] = usePostRoomBlockingMutation();
  const [updateBlocking] = usePutRoomBlockingMutation();
  const [saveBookingSharer] = usePostBookingSharerMutation();
  const [deleteBookingSharer] = useDeleteBookingSharerMutation();
  const [postCASTransactionAPI, { isLoading: isLoadingSI }] =
    usePostCASTransactionMutation();
  const [cancelBooking] = useCancelBookingMutation();
  const [bookingConfirmStatus] = usePostBookingConfirmStatusMutation();

  // add POST SI on loading state check out
  const isLoadingCheckOut = isLoadingCOut || isLoadingSI;
  // Define a selector to extract the propertyID from the Redux store
  const selectedPropertyID = useAppSelector(
    (state) => state.properties.selectedProperty.value,
  );

  const [selectedTransId, setSelectedTransId] = useState<string>('');
  const [tracesMessage, setTracesMessage] = useState<string>('');
  const [balanceMessage, setBalanceMessage] = useState<string>('');
  const [siNo, setSINo] = useState<string>('');
  const [vsiNo, setVSINo] = useState<string>('');
  const tracesDialogDisclosure = useDisclosure();
  const scheduleDisclosure = useDisclosure();
  const printFolioDialogDisclosure = useDisclosure();
  const balanceDialogDisclosure = useDisclosure();
  const printSIDialogDisclosure = useDisclosure();
  const printVSIDialogDisclosure = useDisclosure();
  const messageDialogDisclosure = useDisclosure();
  const [messageDialogTitle, setMessageDialogTitle] =
    useState<string>('Warning');
  const [messageDialogDesc, setMessageDialogDesc] = useState<string>(
    'Something went wrong',
  );
  const [messageDialogStatus, setMessageDialogStatus] =
    useState<string>('warning');

  const moduleSelected = useCurrentModule();
  const setSelectedTransactionId = (id: string) => setSelectedTransId(id);

  const { refreshList } = props ? props : useDeclareNewDayContext();

  /**
   * Handles the check-out process for a booking.
   *
   * @param {boolean} skipValidationDate - Whether to skip date validation.
   * @param {boolean} skipValidationTraces - Whether to skip traces validation.
   * @param {boolean} sendFolioToEmail - Whether to send the folio to email.
   */
  const handleCheckOut = async (
    skipValidationDate?: boolean,
    skipValidationTraces?: boolean,
    sendFolioToEmail?: boolean,
    skipPostSI?: boolean,
  ) => {
    const jsonBody: CheckOutTransactionBody = {
      properties: [
        {
          property: {
            propertyID: selectedPropertyID,
            checkouts: [
              {
                checkout: {
                  booking_reference_id: selectedTransId!,
                  checkout_type: 'Regular',
                  skip_validation_date: skipValidationDate || false,
                  skip_validation_traces: skipValidationTraces || false,
                },
              },
            ],
          },
        },
      ],
    };

    //API Call
    try {
      const result = await postCheckOut(jsonBody).unwrap();

      switch (result?.data?.checkOutResponse?.[0]?.code) {
        case 401:
          // Traces
          setTracesMessage(result?.data?.checkOutResponse?.[0]?.response);
          tracesDialogDisclosure.onOpen();
          break;
        case 402:
          // Check out date
          scheduleDisclosure.onOpen();
          break;
        case 403:
          // Balance
          setBalanceMessage(result?.data?.checkOutResponse?.[0]?.response);
          balanceDialogDisclosure.onOpen();
          break;
        case 500:
          // Error in API
          toast({
            severity: 'error',
            message: 'There were errors encountered upon check out.',
          });
          break;
        default:
          try {
            if (!skipPostSI) {
              const cas_id = await postCASTransaction('SI', selectedTransId);
              if (cas_id) {
                setSINo(cas_id);
                // printSIDialogDisclosure.onOpen();
              }
            }
          } catch (error) {
            console.error('Failed to post CAS transaction:', error);
          }
          toast({
            severity: 'success',
            message: 'Check Out was successful. Thank you!',
          });

          //refresh data Declare New Day
          refreshList;

          return true;
      }
    } catch (err) {
      toast({
        severity: 'error',
        message: `An error occurred during check out.\n ${err}` || '',
      });
    } finally {
      sendFolioToEmail && console.log('Send Folio to Email');
    }
  };

  /**
   * Posts a CAS transaction.
   *
   * @param {string} cas_type - The type of CAS transaction.
   * @param {number | string} transaction_id - The ID of the transaction.
   * @returns {Promise<number>} The result of the CAS transaction.
   */
  const postCASTransaction = async (
    cas_type: string,
    transaction_id: number | string,
  ): Promise<string> => {
    const jsonCASBody: CASPostTransaction = {
      properties: [
        {
          property: {
            propertyID: selectedPropertyID,
            cas_transaction: [
              {
                module_code: moduleSelected,
                cas_type: cas_type,
                source: 'Room Posting',
                transaction_id: transaction_id,
                guest_id: 0,
              },
            ],
          },
        },
      ],
    };

    try {
      const casResponse = await postCASTransactionAPI(jsonCASBody).unwrap();
      // Extract the casResponse
      if (casResponse?.data?.[0]?.[0]?.result) {
        return casResponse.data[0][0].result;
      } else {
        return '';
      }
    } catch (err) {
      toast({
        severity: 'error',
        message: `Failed to create Sales Invoice \n${err}`,
      });
      throw err;
    }
  };

  /**
   * Handles the pending check-out process for a booking.
   *
   * @param {boolean} skipValidationDate - Whether to skip date validation.
   * @param {boolean} skipValidationTraces - Whether to skip traces validation.
   */
  const handlePendingCheckout = async (
    skipValidationDate?: boolean,
    skipValidationTraces?: boolean,
  ): Promise<boolean | undefined> => {
    return new Promise((resolve) => {
      openSecCodeDialog(async (result) => {
        if (!result?.data.isValid) {
          return resolve(undefined);
        }

        const jsonBody: CheckOutTransactionBody = {
          properties: [
            {
              property: {
                propertyID: selectedPropertyID,
                checkouts: [
                  {
                    checkout: {
                      booking_reference_id: selectedTransId!,
                      checkout_type: 'Pending',
                      skip_validation_date: skipValidationDate || false,
                      skip_validation_traces: skipValidationTraces || false,
                    },
                  },
                ],
              },
            },
          ],
        };

        //API Call
        try {
          const result = await postCheckOut(jsonBody).unwrap();
          switch (result.data.checkOutResponse.code) {
            case 401:
              // Traces
              setTracesMessage(result.data.checkOutResponse.response);
              tracesDialogDisclosure.onOpen();
              break;
            case 402:
              // Check out date
              scheduleDisclosure.onOpen();
              break;
            case 403:
              // Balance
              setBalanceMessage(result.data.checkOutResponse.response);
              balanceDialogDisclosure.onOpen();
              break;
            case 500:
              // Error in API
              toast({
                severity: 'error',
                message: 'There were errors encountered upon check out.',
              });
              break;
            default:
              toast({
                severity: 'success',
                message: 'Check Out Pending was successful.',
              });
              return resolve(true);
          }
        } catch (err) {
          toast({
            severity: 'error',
            message: err,
          });
        }
        resolve(undefined);
      }, 'allow_pending');
    });
  };

  /**
   * Handles the toast messages for API responses.
   *
   * @param {Object} res - The API response object.
   * @param {Function} setStatus - Optional function to set the status.
   */
  type OldItem = {
    bm_id: number;
    reference_id: string;
    is_success: number;
    return_message: string;
    status: string;
  };

  type ErrorRes = {
    status: number;
    message: string;
    error: true;
    errorLevel?: 'critical' | 'warning' | 'info';
    errors?: {
      error: {
        propertyName: string;
        remarks: string;
        fields: { reference_id: string }[];
      };
    }[];
    title?: string;
  };

  type SuccessRes = {
    status?: number;
    message?: string;
    error?: false;
    data: {
      data: OldItem[] | string[];
    };
  };

  type HandleToastRes = ErrorRes | SuccessRes;

  const handleToast = async (
    res: HandleToastRes,
    setStatus?: (param: boolean) => void,
  ) => {
    const pathname = window.location.pathname;
    // Error envelope shape: has top-level error flag but no data.data
    if (res.error === true && !('data' in res)) {
      setStatus?.(false);
      if (pathname === '/front-office/bookings/masterlist') {
        const processDialogDesc = res.message.includes('Out of Order')
          ? 'The assigned room is marked as OOO. Please assign an available room before proceeding.'
          : res.message;
        setMessageDialogDesc(processDialogDesc);
        setMessageDialogTitle('Check-in cannot be completed');
        setMessageDialogStatus('warning');
        messageDialogDisclosure.onOpen();
      } else {
        await toast({
          severity: 'warning',
          message: res.message || 'Something went wrong',
        });
      }
      return;
    }

    const data = (res as SuccessRes)?.data?.data;
    if (!data || data.length === 0) return;

    // New shape: array of ID strings
    if (typeof data[0] === 'string') {
      setStatus?.(true);
      await toast({
        severity: 'success',
        message: 'Success check in booking',
      });
      return;
    }

    // Old shape: array of objects with per-item status
    const oldData = data as OldItem[];
    const successMsg = oldData.filter((item) => item.status !== 'Error');
    const errorMsg = oldData.filter((item) => item.status === 'Error');

    const arrSuccessMsg = successMsg.map((item) => `${item.return_message}`);
    const arrErrorMsg = errorMsg.map((item) => `${item.return_message}`);

    if (arrSuccessMsg.length > 0) {
      setStatus?.(true);
      await toast({
        severity: 'success',
        message: 'Success check in booking',
      });
    }
    if (arrErrorMsg.length > 0) {
      setStatus?.(false);
      await toast({ severity: 'warning', message: arrErrorMsg.join('</br>') });
    }
  };

  /**
   * Handles the check-in process for a booking.
   *
   * @param {string} roomNumber - The room number for check-in.
   * @param {string} checkinDate - The check-in date.
   * @param {string} checkoutDate - The check-out date.
   * @param {Object} obj - Optional object containing additional data.
   * @returns {Promise<boolean>} Whether the check-in was successful.
   */
  const handleCheckIn = async (
    roomNumber?: string,
    checkinDate?: string,
    checkoutDate?: string,
    obj?: { refId: string },
  ) => {
    let isSuccess: boolean = false;
    startLoading();
    const checkInObj: IFOBookingCheckInPayload = {
      properties: [
        {
          property: {
            propertyID: selectedPropertyID,
            withLockset: withLockset,
            bookings: [
              {
                booking: [
                  {
                    reference_id: obj?.refId || selectedTransId!,
                    roomNumber: roomNumber,
                    checkinDate: checkinDate,
                    checkoutDate: checkoutDate,
                  },
                ],
              },
            ],
          },
        },
      ],
    };

    await checkIn(checkInObj)
      .unwrap()
      .then(async (res: any) => {
        await handleToast(res, (status) => (isSuccess = status));
      })
      .catch((err) => {
        toast({
          severity: 'error',
          message: err,
        });
      })
      .finally(stopLoading);
    stopLoading();
    return isSuccess;
  };

  /**
   * Handles the check-in process for multiple bookings.
   * @param {string[] | GridRowSelectionModel} bookings - Array of booking reference IDs or GridRowSelectionModel.
   * @returns {Promise<void>}
   */
  const handleMultiCheckIn = async (
    bookings: string[] | GridRowSelectionModel,
  ) => {
    const checkInObj: IFOBookingCheckInPayload = {
      properties: [
        {
          property: {
            propertyID: selectedPropertyID,
            withLockset: withLockset,
            bookings: bookings.map((booking) => ({
              booking: [
                {
                  reference_id: String(booking),
                },
              ],
            })),
          },
        },
      ],
    };

    await checkIn(checkInObj)
      .unwrap()
      .then(async (res: any) => await handleToast(res))
      .catch((err) => {
        toast({
          severity: 'error',
          message: err,
        });
      });
  };

  /**
   * Handles the re-check-in process for a booking.
   * @param {string} bookingRefId - Booking reference ID.
   * @param {BookingsParamsType} bookingStatus - Booking status.
   * @returns {Promise<string>} The booking reference ID.
   */
  const handleUndoCheckOut = async (
    bookingRefId: string,
    bookingStatus: BookingsParamsType,
  ): Promise<string> => {
    try {
      startLoading();
      const result = await putUndoCheckOut({
        bookingRefId,
        bookingStatus,
      }).unwrap();
      const voidedSalesInvoice = result?.data?.result[0]?.voidedSalesInvoice;
      console.log('voidedSalesInvoice', voidedSalesInvoice);
      if (voidedSalesInvoice) {
        setVSINo(voidedSalesInvoice);
        printVSIDialogDisclosure.onOpen();
      }
      console.log('VSINO', voidedSalesInvoice);
      toast({
        severity: 'success',
        message: 'Guest was Re-Checked In successfully',
      });
    } catch (err) {
      toast({
        severity: 'error',
        message: err,
      });
    } finally {
      stopLoading();
    }
    return bookingRefId;
  };

  /**
   * Handles the Undo Check in process for a booking.
   */
  const handleUndoCheckIn = async (
    bookingRefId: string,
    roomStatus: string,
  ): Promise<string> => {
    await putUndoCheckIn({ bookingRefId, roomStatus })
      .unwrap()
      .then(() => {
        toast({
          severity: 'success',
          message: 'Check-in was reversed successfully',
        });
      })
      .catch((err) => {
        toast({
          severity: 'error',
          message: err,
        });
      })
      .finally(() => {
        stopLoading();
      });
    return bookingRefId;
  };

  /**
   * Handles unblocking a room.
   * @param {string} param - Room parameter for unblocking.
   * @returns {Promise<boolean>} Whether the unblocking was successful.
   */
  const handleUnblockingRoom = async (param: string) => {
    let isSuccess: boolean = false;
    startLoading();
    await unblockRoom(param)
      .unwrap()
      .then(() => {
        toast({
          severity: 'success',
          message: 'Success unmark room blocking',
        });
        isSuccess = true;
      })
      .catch((err) => toast({ severity: 'error', message: err }))
      .finally(() => {
        stopLoading();
      });
    stopLoading();
    return isSuccess;
  };

  /**
   * Handles creating room blocking.
   * @param {FOCreateBlockingsPayloadInterface['properties'][0]['property']['blockings']} payload - Room blocking payload.
   * @returns {Promise<boolean>} Whether the room blocking creation was successful.
   */
  const handleCreateRoomBlocking = async (
    payload: FOCreateBlockingsPayloadInterface['properties'][0]['property']['blockings'],
  ) => {
    let isSuccess: boolean = false;
    startLoading();
    await createBlocking({
      properties: [
        {
          property: {
            propertyID: selectedPropertyID,
            blockings: payload?.map(({ blocking }) => ({
              blocking: {
                ...blocking,
                signature: SHA512(
                  `${selectedPropertyID}${blocking.description}${blocking.date_start}${blocking.date_end}${blocking.room_code}${blocking.room_number}${blocking.status}`,
                ).toString(),
              },
            })),
          },
        },
      ],
    })
      .unwrap()
      .then(() => {
        toast({
          severity: 'success',
          message: 'Success creating room blocking',
        });
        isSuccess = true;
      })
      .catch((err) => toast({ severity: 'error', message: err }))
      .finally(() => {
        stopLoading();
      });
    stopLoading();
    return isSuccess;
  };

  /**
   * Handles updating room blocking.
   * @param {{id: string, payload: FOCreateBlockingsPayloadInterface['properties'][0]['property']['blockings']}} data - Room blocking update data.
   * @returns {Promise<boolean>} Whether the room blocking update was successful.
   */
  const handleUpdateRoomBlocking = async (data: {
    id: string;
    payload: FOCreateBlockingsPayloadInterface['properties'][0]['property']['blockings'];
  }) => {
    let isSuccess: boolean = false;
    startLoading();
    await updateBlocking({
      id: data.id,
      payload: {
        properties: [
          {
            property: {
              propertyID: selectedPropertyID,
              blockings: data.payload?.map(({ blocking }) => ({
                blocking: {
                  ...blocking,
                  signature: SHA512(
                    `${selectedPropertyID}${blocking.description}${blocking.date_start}${blocking.date_end}${blocking.room_code}${blocking.room_number}${blocking.status}`,
                  ).toString(),
                },
              })),
            },
          },
        ],
      },
    })
      .unwrap()
      .then(() => {
        toast({
          severity: 'success',
          message: 'Success updating room blocking',
        });
        isSuccess = true;
      })
      .catch((err) => toast({ severity: 'error', message: err }))
      .finally(() => {
        stopLoading();
      });
    stopLoading();
    return isSuccess;
  };

  /**
   * Handles saving booking sharer information.
   * @param {{id: string, payload: FOCreateBookingSharerPayloadInterface['properties'][0]['property']['sharers']}} data - Booking sharer data.
   * @returns {Promise<boolean>} Whether the booking sharer save was successful.
   */
  type SaveBookingSharerErrorRes = {
    status?: number;
    message?: string;
    error?: true;
    title?: string;
  };

  type SaveBookingSharerSuccessRes = {
    status?: number;
    message?: string;
    error?: false;
    data?: unknown;
  };

  type SaveBookingSharerRes =
    | SaveBookingSharerErrorRes
    | SaveBookingSharerSuccessRes;

  const handleSaveBookingSharer = async (data: {
    id: string;
    payload: FOCreateBookingSharerPayloadInterface['properties'][0]['property']['sharers'];
  }) => {
    let isSuccess = false;

    startLoading();

    try {
      const res: SaveBookingSharerRes = await saveBookingSharer({
        id: data.id,
        payload: {
          properties: [
            {
              property: {
                propertyID: selectedPropertyID,
                sharers: data.payload?.map(({ sharer }) => ({
                  sharer: {
                    ...sharer,
                    ...(sharer.guestReferenceID
                      ? {
                          signature: SHA512(
                            `${selectedPropertyID}${sharer.guestReferenceID}${
                              sharer.isChild ? 1 : 0
                            }${sharer.subFolio}`,
                          ).toString(),
                        }
                      : {
                          signature: SHA512(
                            `${selectedPropertyID}${sharer.guestName}${
                              sharer.emailAddress
                            }${sharer.contactNumber}${sharer.nationality}${
                              sharer.isChild ? 1 : 0
                            }${sharer.subFolio}`,
                          ).toString(),
                        }),
                  },
                })),
              },
            },
          ],
        },
      }).unwrap();

      if (res?.error === true) {
        toast({
          severity: 'warning',
          message: res.message || res.title || 'Something went wrong',
        });

        return false;
      }

      toast({
        severity: 'success',
        message: 'Added room sharer(s) successfully.',
      });

      isSuccess = true;
    } catch (err) {
      const error = err as SaveBookingSharerErrorRes;

      toast({
        severity: 'warning',
        message: error?.message || error?.title || 'Something went wrong',
      });
    } finally {
      stopLoading();
    }

    return isSuccess;
  };
  /**
   * Handles deleting booking sharer information.
   * @param {{id: string, payload: FODeleteBookingSharerPayloadInterface['properties'][0]['property']['sharers']}} data - Booking sharer deletion data.
   * @returns {Promise<boolean>} Whether the booking sharer deletion was successful.
   */
  const handleDeleteBookingSharer = async (data: {
    id: string;
    payload: FODeleteBookingSharerPayloadInterface['properties'][0]['property']['sharers'];
  }) => {
    let isSuccess: boolean = false;
    startLoading();
    await deleteBookingSharer({
      id: data.id,
      payload: {
        properties: [
          {
            property: {
              propertyID: selectedPropertyID,
              sharers: data.payload?.map(({ sharer }) => ({
                sharer: {
                  ...sharer,
                  signature: SHA512(
                    `${selectedPropertyID}${sharer.guestReferenceID}`,
                  ).toString(),
                },
              })),
            },
          },
        ],
      },
    })
      .unwrap()
      .then(() => {
        toast({
          severity: 'success',
          message: 'Success deleting booking sharer',
        });
        isSuccess = true;
      })
      .catch((err) => toast({ severity: 'error', message: err }))
      .finally(() => {
        stopLoading();
      });
    stopLoading();
    return isSuccess;
  };

  /**
   * Handles cancelling bookings.
   * @param {CancelReservationReason} data - Cancellation reason data.
   * @param {string} referenceId - Booking reference ID.
   * @returns {Promise<boolean>} Whether the booking cancellation was successful.
   */
  const handleCancelBookings = async (
    data: CancelReservationReason,
    referenceId: string,
  ) => {
    let isSuccess: boolean = false;
    const { reason, remarks } = data;
    const signature = SHA512(
      selectedPropertyID + referenceId + reason + remarks,
    ).toString();
    const jsonData = {
      properties: [
        {
          property: {
            propertyID: selectedPropertyID,
            cancelBookings: [
              {
                cancelBooking: {
                  reference_id: referenceId,
                  cancellation_reason: reason,
                  cancellation_remarks: remarks,
                  signature: signature,
                },
              },
            ],
          },
        },
      ],
    };

    await cancelBooking(jsonData)
      .unwrap()
      .then((response) => {
        toast({
          severity: response.status === 200 ? 'success' : response.errorLevel,
          message: response.message,
        });
        isSuccess = true;
      })
      .catch((err) => {
        toast({
          severity: 'error',
          message: err,
        });
        stopLoading();
      })
      .finally(() => {
        stopLoading();
      });
    return isSuccess;
  };

  /**
   * Handles confirming the status of a booking.
   * @param {string} referenceId - Booking reference ID.
   * @param {boolean} status - Confirmation status.
   * @returns {Promise<boolean>} Whether the status confirmation was successful.
   */
  const handleConfirmStatusBooking = async (
    referenceId: string,
    status: boolean,
  ) => {
    let isSuccess: boolean = false;
    if (!isValidUUID(referenceId)) {
      isSuccess = false;
      toast({ message: 'Invalid reference ID' });
      return isSuccess;
    }
    startLoading();
    await bookingConfirmStatus({ refId: referenceId, status })
      .unwrap()
      .then(() => {
        toast({
          severity: 'success',
          message: 'Reservation status successfully updated.',
        });
        isSuccess = true;
      })
      .catch((err) => toast({ severity: 'error', message: err }))
      .finally(() => {
        stopLoading();
      });
    stopLoading();
    return isSuccess;
  };

  return {
    handleCheckOut,
    handlePendingCheckout,
    setSelectedTransactionId,
    tracesMessage,
    balanceMessage,
    tracesDialogDisclosure,
    scheduleDisclosure,
    printFolioDialogDisclosure,
    balanceDialogDisclosure,
    isLoadingCheckOut,
    handleUndoCheckOut,
    handleUndoCheckIn,
    handleCheckIn,
    handleMultiCheckIn,
    handleUnblockingRoom,
    handleCreateRoomBlocking,
    handleUpdateRoomBlocking,
    handleSaveBookingSharer,
    handleDeleteBookingSharer,
    handleCancelBookings,
    handleConfirmStatusBooking,
    printSIDialogDisclosure,
    siNo,
    printVSIDialogDisclosure,
    vsiNo,
    messageDialogDisclosure,
    messageDialogTitle,
    messageDialogDesc,
    messageDialogStatus,
  };
};

export default useBookings;
